import { useReducer, useRef, useCallback, useEffect } from "react";
import type { Dispatch } from "react";

export type { Dispatch };
import AuthUtils from "@/providers/firebase/authUtils";
import type {
  WizardState,
  WizardAction,
  BuildScope,
  RoleKey,
  ExtractedReview,
  DetectedPerson,
  ManualSearchCandidate,
  ManualSearchClarification,
} from "../types";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";
const WS_URL  = import.meta.env.VITE_WS_URL  || "ws://localhost:8000";

// ── Reducer ───────────────────────────────────────────────────────────────────

/**
 * Build use-case options from ED21 Domain values in the entity dimension index.
 * These are the business domains Raisy can help with — they are only asked because
 * they cannot be reliably inferred from a website scrape.
 *
 * Returns [] until the index has been fetched: it is DB-kernel-owned with no bundled
 * fallback, so an empty list here means "still loading", never "no domains".
 */
function buildUseCaseOptionsFromIndex(entityIndex?: Record<string, unknown> | null): WizardState["useCaseOptions"] {
  const dimensions = (entityIndex as any)?.dimensions ?? {};
  const ed21 = dimensions.ED21;
  const defaults = ed21?.default_values ?? {};
  return Object.entries(defaults).map(([key, description]) => ({
    key: key.toLowerCase().replace(/\s+/g, "_"),
    label: key,
    description: typeof description === "string" ? description : "",
  }));
}

const initialState: WizardState = {
  step: 1,
  direction: 1,
  url: "",
  urlValidation: null,
  isValidatingUrl: false,
  extractionPct: 0,
  extractionMessage: "",
  isExtracting: false,
  extractedReview: null,
  detectedPersonEntities: [],
  selfMatchDecision: null,
  duplicateDecision: null,
  candidatesConfirmed: false,
  isConfirmingCandidates: false,
  confirmCandidatesError: null,
  visibility: "public",
  entityTypeHint: "company",
  // QUICK SETUP IS THE DEFAULT (PO, 2026-09-23). A full scan costs the user up to a minute
  // and it is the TALKATIVE page that pays for it — the wait scales with how many products and
  // people the page happens to mention, which the user cannot predict and did not ask for. So
  // the cheap, predictable scan is what happens unless he asks for more; the full scan is one
  // switch away, here and again on the entity page.
  quickScrape: true,
  isExternal: false,
  selectedRoles: [],
  roleOther: "",
  useCaseOptions: buildUseCaseOptionsFromIndex(),
  selectedUseCases: [],
  revenueStage: "",
  // D-061 ship narrow: equity-raise is the default journey (single-source gate: types.ENABLED_JOURNEYS).
  firstTasks: ["raise_money"],
  firstTaskOther: "",
  entityId: null,
  isSubmitting: false,
  processId: null,
  error: null,
  // D-061 ship narrow: Standard build depth is the default (Recommended), not asked in v1.
  buildScope: "standard" as BuildScope,
  entityDetails: null,
  relatedEntities: [],
  manualSearchStatus: "idle",
  manualSearchCandidates: [],
  manualSearchQuestions: [],
  manualSearchSelectedIndex: null,
  manualSearchClarifications: [],
  manualSearchError: null,
  manualEscapeHatch: false,
};

function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case "SET_STEP":
      return { ...state, step: action.step, direction: action.direction, error: null };
    case "SET_URL":
      return { ...state, url: action.url, urlValidation: null };
    case "SET_URL_VALIDATION":
      return { ...state, urlValidation: action.result, isValidatingUrl: false };
    case "SET_VALIDATING_URL":
      return { ...state, isValidatingUrl: action.value };
    case "SET_EXTRACTION_PROGRESS":
      return { ...state, extractionPct: action.pct, extractionMessage: action.message };
    case "SET_EXTRACTING":
      return { ...state, isExtracting: action.value, extractionPct: action.value ? 0 : state.extractionPct };
    case "SET_EXTRACTED_REVIEW":
      return {
        ...state,
        extractedReview: action.review,
        candidatesConfirmed: false,
        confirmCandidatesError: null,
      };
    case "ADD_DETECTED_PERSON":
      return { ...state, detectedPersonEntities: [...state.detectedPersonEntities, action.person] };
    case "SET_SELF_MATCH":
      return { ...state, selfMatchDecision: action.decision };
    case "SET_DUPLICATE_DECISION":
      return { ...state, duplicateDecision: action.decision };
    case "TOGGLE_CANDIDATE": {
      if (!state.extractedReview) return state;
      const sub_entities = state.extractedReview.sub_entities.map((s, i) =>
        i === action.index ? { ...s, accepted: !s.accepted } : s,
      );
      return { ...state, extractedReview: { ...state.extractedReview, sub_entities } };
    }
    case "SET_CONFIRMING_CANDIDATES":
      return { ...state, isConfirmingCandidates: action.value };
    case "SET_CANDIDATES_CONFIRMED":
      return { ...state, candidatesConfirmed: action.value };
    case "SET_CONFIRM_CANDIDATES_ERROR":
      return { ...state, confirmCandidatesError: action.error };
    case "SET_VISIBILITY":
      return { ...state, visibility: action.value };
    case "SET_ENTITY_TYPE_HINT":
      return { ...state, entityTypeHint: action.value };
    case "SET_QUICK_SCRAPE":
      return { ...state, quickScrape: action.value };
    case "SET_IS_EXTERNAL":
      return { ...state, isExternal: action.value };
    case "SET_ROLE": {
      const has = state.selectedRoles.includes(action.role);
      return { ...state, selectedRoles: has ? state.selectedRoles.filter((r) => r !== action.role) : [...state.selectedRoles, action.role] };
    }
    case "SET_ROLE_OTHER":
      return { ...state, roleOther: action.value };
    case "SET_USE_CASE_OPTIONS":
      return { ...state, useCaseOptions: action.options };
    case "TOGGLE_USE_CASE": {
      const has = state.selectedUseCases.includes(action.key);
      return {
        ...state,
        selectedUseCases: has
          ? state.selectedUseCases.filter((k) => k !== action.key)
          : [...state.selectedUseCases, action.key],
      };
    }
    case "SET_ENTITY_DETAILS":
      return { ...state, entityDetails: action.details };
    case "PATCH_ENTITY_DETAILS":
      return { ...state, entityDetails: state.entityDetails ? { ...state.entityDetails, ...action.patch } : null };
    case "INIT_RELATED_ENTITIES":
      return { ...state, relatedEntities: action.entities };
    case "ADD_RELATED_ENTITY":
      return { ...state, relatedEntities: [...(state.relatedEntities ?? []), action.entity] };
    case "UPDATE_RELATED_ENTITY": {
      const updated = (state.relatedEntities ?? []).map((e, i) =>
        i === action.index ? { ...e, ...action.patch } : e,
      );
      return { ...state, relatedEntities: updated };
    }
    case "SET_SUBMITTING":
      return { ...state, isSubmitting: action.value };
    case "SET_PROCESS_ID":
      return { ...state, processId: action.processId };
    case "SET_ENTITY_ID":
      return { ...state, entityId: action.entityId };
    case "SET_ERROR":
      return { ...state, error: action.error };
    case "SET_BUILD_SCOPE":
      return { ...state, buildScope: action.value };
    case "SET_REVENUE_STAGE":
      return { ...state, revenueStage: action.value };
    case "TOGGLE_FIRST_TASK": {
      const has = state.firstTasks.includes(action.value);
      return {
        ...state,
        firstTasks: has
          ? state.firstTasks.filter((t) => t !== action.value)
          : [...state.firstTasks, action.value],
      };
    }
    case "SET_FIRST_TASK_OTHER":
      return { ...state, firstTaskOther: action.value };
    case "SET_MANUAL_SEARCH_STATUS":
      return { ...state, manualSearchStatus: action.value };
    case "SET_MANUAL_SEARCH_RESULT":
      return {
        ...state,
        manualSearchCandidates: action.candidates,
        manualSearchQuestions: action.questions,
        manualSearchStatus: action.candidates.length > 0 ? "resolved" : "ambiguous",
        manualSearchSelectedIndex: null,
      };
    case "SELECT_MANUAL_CANDIDATE":
      return {
        ...state,
        manualSearchSelectedIndex: action.index,
        manualEscapeHatch: action.index !== null ? false : state.manualEscapeHatch,
      };
    case "ADD_MANUAL_CLARIFICATION":
      return {
        ...state,
        manualSearchClarifications: [
          ...state.manualSearchClarifications,
          { question: action.question, answer: action.answer },
        ],
      };
    case "SET_MANUAL_SEARCH_ERROR":
      return { ...state, manualSearchError: action.error, manualSearchStatus: action.error ? "error" : state.manualSearchStatus };
    case "SET_MANUAL_ESCAPE_HATCH":
      return {
        ...state,
        manualEscapeHatch: action.value,
        manualSearchSelectedIndex: action.value ? null : state.manualSearchSelectedIndex,
      };
    case "RESET_MANUAL_SEARCH":
      return {
        ...state,
        manualSearchStatus: "idle",
        manualSearchCandidates: [],
        manualSearchQuestions: [],
        manualSearchSelectedIndex: null,
        manualSearchClarifications: [],
        manualSearchError: null,
        manualEscapeHatch: false,
      };
    case "RESET":
      return initialState;
    default:
      return state;
  }
}

// ── Shape entity WS extraction data into ExtractedReview ─────────────────────

function shapeEntityExtractionData(entity: Record<string, any>, candidates: any[]): ExtractedReview {
  const confidence = typeof entity.confidence_score === "number" ? entity.confidence_score : 0.7;
  const toDots = (c: number) => Math.max(1, Math.min(5, Math.round(c * 5)));

  return {
    primary_entity: {
      suggested_name: entity.entity_name || "Unknown",
      entity_type: entity.entity_type || "company",
      entity_type_sub: entity.entity_type_sub ?? null,
      entity_offering_type: entity.entity_offering_type ?? null,
      entity_offering_delivery: entity.entity_offering_delivery ?? null,
      description: entity.entity_description ?? null,
      industry: entity.industry ?? null,
      target_market: entity.target_market ?? null,
      brand_tone: entity.brand_tone ?? null,
      confidence,
      confidence_dots: toDots(confidence),
      low_confidence: confidence < 0.6,
    },
    // These are review candidates from the BE's node_extracted event — NOT yet persisted.
    // The Review step (Step4Review) shows them as a checklist, pre-accepted, and only
    // creates the ones still accepted via POST /entity/{id}/extraction/confirm (backlog
    // #18 discovery review step).
    sub_entities: (candidates ?? []).map((c) => {
      const sc: number = c.confidence_score ?? c.confidence ?? 0.65;
      return {
        suggested_name: c.entity_name || c.name || "Unknown",
        entity_type: c.entity_type || c.type || "product",
        entity_type_sub: c.entity_type_sub ?? null,
        entity_offering_type: c.entity_offering_type ?? null,
        entity_offering_delivery: c.entity_offering_delivery ?? null,
        description: c.entity_description || c.evidence || null,
        confidence: sc,
        confidence_dots: toDots(sc),
        is_part_of_parent: c.is_part_of_parent !== false,
        relationship_type: c.relationship_type || "parent_child",
        role_title: c.role_title ?? null,
        accepted: true,
      };
    }),
    has_duplicate_warning: false,
  };
}

// Folds a candidate's affiliation/role into its stored description — there's no dedicated
// "affiliation" column on Entity, and this text is what generate_entity_embedding embeds,
// so it's the difference between two same-named people (e.g. two "Chaithanya"s in different
// roles) being distinguishable by future dedup search or just blank, identical rows.
// Shared by createEntityFromManualCandidate (what actually gets persisted) and
// Wizard_Entity_Onboarding's merge-review candidateData (what gets diffed against a possible
// existing match) so the two never drift apart.
export function buildCandidateDescription(
  candidate: Pick<ManualSearchCandidate, "evidence" | "role" | "affiliation"> | null,
  fallbackDescription: string,
): string {
  const base = candidate?.evidence || fallbackDescription;
  const context = [
    candidate?.role,
    candidate?.affiliation ? `at ${candidate.affiliation}` : null,
  ].filter(Boolean).join(" ");
  return context ? `${base} (${context})` : base;
}

// ── Dedup check for the escape-hatch path ("use my description as-is") ────────
// The AI search-resolver's own existing_matches (see entity_search_resolver_service.py)
// is the ONLY duplicate guard for person entities — create_entity's own guard skips
// entity_type=="person" entirely ("people legitimately share names"). That guard only
// ever runs per LLM candidate, though — when the search comes back ambiguous (0
// candidates, e.g. "found the company but not this specific person there") or the user
// picks the escape hatch, there's no candidate for it to attach to, so a person could be
// created a second (third, fourth...) time with zero duplicate check at all. This reuses
// the existing /entity/sem_search endpoint (same find_similar_entities call, just not
// nested under a resolver candidate) so the escape-hatch path gets the same protection.
export async function checkExistingMatches(
  name: string,
  description: string,
  entityType: string,
): Promise<import("../types").ExistingEntityMatch[]> {
  try {
    const headers = await AuthUtils.getAuthHeaders();
    const res = await fetch(`${API_URL}/api/v1/entity/sem_search`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...headers },
      body: JSON.stringify({
        name, description, entity_type: entityType,
        top_n: 5, threshold: 0.80, app: "raisy",
      }),
    });
    if (!res.ok) return [];
    const body = await res.json().catch(() => ({}));
    const candidates: Array<{ entity: Record<string, unknown>; score: number }> = body.data?.candidates ?? [];
    return candidates.map((c) => ({
      entity_id: c.entity.entity_id as string,
      entity_name: c.entity.entity_name as string,
      entity_type: c.entity.entity_type as string,
      entity_description: (c.entity.entity_description as string) ?? null,
      person_role: (c.entity.person_role as string) ?? null,
      entity_location: (c.entity.entity_location as string) ?? null,
      score: c.score,
    }));
  } catch {
    // Degrade like dedup_check_skipped elsewhere: don't block creation on this being down.
    return [];
  }
}

// ── Shape a manual-search candidate (or raw description) into ExtractedReview ──
// PROJECT-077 — sibling of shapeEntityExtractionData above, sourced from the AI
// web-search resolver instead of a WS node_extracted event. sub_entities is always
// empty: there's no further discovery to review for a manually-resolved identity.

export function shapeManualCandidateReview(
  candidate: ManualSearchCandidate | null,
  description: string,
  entityTypeHint: string,
): ExtractedReview {
  const confidence = candidate?.confidence_score ?? 0.5;
  const toDots = (c: number) => Math.max(1, Math.min(5, Math.round(c * 5)));

  return {
    primary_entity: {
      suggested_name: candidate?.entity_name || "Unknown",
      entity_type: candidate?.entity_type || entityTypeHint || "company",
      entity_type_sub: candidate?.entity_type_sub ?? null,
      entity_offering_type: candidate?.entity_offering_type ?? null,
      entity_offering_delivery: candidate?.entity_offering_delivery ?? null,
      description: candidate?.evidence ?? description ?? null,
      industry: null,
      target_market: null,
      brand_tone: null,
      confidence,
      confidence_dots: toDots(confidence),
      low_confidence: confidence < 0.6,
    },
    sub_entities: [],
    has_duplicate_warning: false,
  };
}

// ── Hook ──────────────────────────────────────────────────────────────────────

interface WizardCallbacks {
  onEntityCreated?: (entity: {
    entity_id: string;
    entity_name: string;
    entity_type: string;
    parent_entity_ids: string[];
  }) => void;
  onClose: () => void;
  onMinimize?: (step: number) => void;
  parentEntityId?: string | null;
}

interface UseWizardEntityOptions extends WizardCallbacks {
  entityIndex?: Record<string, unknown> | null;
}


export function useWizardEntity(callbacks: UseWizardEntityOptions) {
  const { entityIndex, parentEntityId, ...wizardCallbacks } = callbacks;
  const [state, dispatch] = useReducer(wizardReducer, {
    ...initialState,
    useCaseOptions: buildUseCaseOptionsFromIndex(entityIndex),
  });

  // Populate/refresh use-case options when the DB-provided index arrives. Usually
  // synchronous (the persisted cache is hydrated before render); on a cold start
  // this fires once the fetch lands.
  useEffect(() => {
    if (entityIndex) {
      dispatch({
        type: "SET_USE_CASE_OPTIONS",
        options: buildUseCaseOptionsFromIndex(entityIndex),
      });
    }
  }, [entityIndex]);

  const wsRef = useRef<WebSocket | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  async function apiFetch(path: string, options: RequestInit = {}) {
    const headers = await AuthUtils.getAuthHeaders();
    const res = await fetch(`${API_URL}/api/v1${path}`, {
      ...options,
      headers: { "Content-Type": "application/json", ...headers, ...(options.headers ?? {}) },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.detail ?? `HTTP ${res.status}`);
    }
    return res.json();
  }

  // ── Navigation ─────────────────────────────────────────────────────────────

  const advanceStep = useCallback((currentState: WizardState) => {
    dispatch({ type: "SET_STEP", step: currentState.step + 1, direction: 1 });
  }, []);

  const back = useCallback((currentStep: number) => {
    dispatch({ type: "SET_STEP", step: currentStep - 1, direction: -1 });
  }, []);

  const minimize = useCallback((currentStep: number) => {
    wizardCallbacks.onMinimize?.(currentStep);
  }, [wizardCallbacks]);

  // ── URL Validation (instant, client-side only) ────────────────────────────
  // The backend validate-url makes a live HTTP HEAD request which takes 2-3s.
  // Duplicate/reachability feedback is surfaced at entity-create time (409 / WS error).

  const validateUrl = useCallback((url: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!url.trim()) {
      dispatch({ type: "SET_URL_VALIDATION", result: null });
      return;
    }
    // Instant format check — no network round-trip
    try {
      const parsed = new URL(url.startsWith("http") ? url : `https://${url}`);
      if (parsed.hostname.includes(".")) {
        dispatch({ type: "SET_URL_VALIDATION", result: { status: "new", normalized_url: parsed.href } });
        return;
      }
    } catch { /* fall through */ }
    dispatch({ type: "SET_URL_VALIDATION", result: null });
  }, [parentEntityId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Step 1 — Create entity + open extraction WS ────────────────────────────

  const startSession = useCallback(async (url: string, mode: "company" | "product" | "service" | "person" = "company", visibility: "public" | "private" = "public"): Promise<string> => {
    // Normalise URL: prepend https:// if no protocol is present
    const normalisedUrl = url.startsWith("http://") || url.startsWith("https://")
      ? url
      : `https://${url}`;

    // Use the URL hostname as the placeholder name so it's unique per site
    let placeholderName = "New Entity";
    try { placeholderName = new URL(normalisedUrl).hostname.replace(/^www\./, ""); } catch { /* invalid url */ }

    const headers = await AuthUtils.getAuthHeaders();
    const createBody: Record<string, unknown> = {
      entity_name: placeholderName,
      entity_type: mode,
      entity_status: "prospect",
      visibility,
    };
    if (parentEntityId) {
      createBody.parent_entity_id = parentEntityId;
    }

    const res = await fetch(`${API_URL}/api/v1/entity/create`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...headers },
      body: JSON.stringify(createBody),
    });

    const body = await res.json().catch(() => ({}));

    // 409 = duplicate entity already exists.
    if (res.status === 409) {
      const similarity: number =
        typeof body.data?.similarity_score === "number"
          ? body.data.similarity_score
          : 1.0;
      const existingId: string = body.data?.existing_entity_id;
      const existingName: string = body.data?.existing_entity?.entity_name ?? "";
      const existingType: string = body.data?.existing_entity?.entity_type ?? mode;

      // Exact match (1.0) = treat as definitive same entity, no question.
      if (similarity >= 1.0) {
        dispatch({ type: "SET_ENTITY_ID", entityId: existingId });
        return existingId;
      }

      // Fuzzy match (< 1.0) = ask the user before deciding.
      dispatch({
        type: "SET_DUPLICATE_DECISION",
        decision: {
          status: "pending",
          existingEntityId: existingId,
          existingEntityName: existingName,
          existingEntityType: existingType,
          similarity,
          createBody,
        },
      });
      throw new Error("DUPLICATE_NEEDS_CONFIRMATION");
    }

    if (!res.ok) throw new Error(body.detail ?? body.message ?? `HTTP ${res.status}`);

    const entityId: string = body.data?.entity_id;
    if (!entityId) throw new Error(body.detail ?? body.message ?? `HTTP ${res.status}`);

    dispatch({ type: "SET_ENTITY_ID", entityId });
    return entityId;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const confirmDuplicate = useCallback(async (forceCreate: boolean): Promise<string> => {
    const decision = stateRef.current.duplicateDecision;
    if (!decision || decision.status !== "pending") {
      throw new Error("No pending duplicate decision");
    }

    if (!forceCreate) {
      // User says: "Yes, it's the same entity."
      dispatch({
        type: "SET_DUPLICATE_DECISION",
        decision: { ...decision, status: "confirmed" },
      });
      dispatch({ type: "SET_ENTITY_ID", entityId: decision.existingEntityId });
      return decision.existingEntityId;
    }

    // User says: "No, create a new entity anyway."
    dispatch({
      type: "SET_DUPLICATE_DECISION",
      decision: { ...decision, status: "creating" },
    });
    const headers = await AuthUtils.getAuthHeaders();
    const res = await fetch(`${API_URL}/api/v1/entity/create`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...headers },
      body: JSON.stringify({ ...decision.createBody, force_create: true }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.detail ?? body.message ?? `HTTP ${res.status}`);
    const entityId: string = body.data?.entity_id;
    if (!entityId) throw new Error("No entity_id returned");

    dispatch({
      type: "SET_DUPLICATE_DECISION",
      decision: { ...decision, status: "confirmed" },
    });
    dispatch({ type: "SET_ENTITY_ID", entityId });
    return entityId;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openExtractionSocket = useCallback(
    async (
      entityId: string,
      extractionUrl: string,
      onAutoAdvance: () => void,
      /** Quick setup — `quick_scrape` on the socket. The entity's own fields are identical
       * either way; this only decides whether the page's other products and people are
       * looked for at all. Default false keeps every existing caller on the full run. */
      quickScrape: boolean = false,
    ) => {
      wsRef.current?.close();
      dispatch({ type: "SET_EXTRACTING", value: true });
      dispatch({ type: "SET_ERROR", error: null });

      const wsToken = await AuthUtils.ensureAuthToken();
      const ws = new WebSocket(
        `${WS_URL}/api/v1/entity/ws/entity/${encodeURIComponent(entityId)}/extract?url=${encodeURIComponent(extractionUrl)}&quick_scrape=${quickScrape ? "true" : "false"}&token=${encodeURIComponent(wsToken)}`,
      );
      wsRef.current = ws;

      let extractionDone = false;
      let pendingReview: import("../types").ExtractedReview | null = null;
      // Which run this actually is, echoed by the socket's own events rather than assumed
      // from the argument — the status lines below describe work that only happens on a full
      // run, and a run whose mode is guessed is a run whose status line can lie.
      let quickRun = quickScrape;

      ws.onmessage = (evt) => {
        try {
          const event = JSON.parse(evt.data as string);

          if (event.event === "node_extracting") {
            quickRun = Boolean(event.quick_scrape);
            dispatch({ type: "SET_EXTRACTION_PROGRESS", pct: 0, message: "Connecting to website…" });
          }

          if (event.event === "extraction_started") {
            // Service stages are 0–100; remap into the first half (0–50%) so entity
            // saves own the second half.
            dispatch({ type: "SET_EXTRACTION_PROGRESS", pct: Math.round((event.pct ?? 0) * 0.5), message: event.message ?? "Reading website content…" });
          }

          if (event.event === "progress") {
            // Remap 0–100 service pct → 0–50 so entity saves own 50–100%
            dispatch({ type: "SET_EXTRACTION_PROGRESS", pct: Math.round((event.pct ?? 0) * 0.5), message: event.message ?? "" });
          }

          if (event.event === "extraction_complete") {
            // LLM done — land at 48% bridge; node_extracted will push to 50%
            dispatch({ type: "SET_EXTRACTION_PROGRESS", pct: 48, message: "Wrapping up extraction…" });
          }

          if (event.event === "node_extracted" && !extractionDone) {
            // 50% mark — LLM done. Non-person candidates are NOT saved yet — the user
            // reviews/excludes them in StepEntityCanvas before anything is created.
            if (event.quick_scrape !== undefined) quickRun = Boolean(event.quick_scrape);
            pendingReview = shapeEntityExtractionData(event.entity ?? {}, event.extraction_candidates ?? []);
            // The review step has to be able to say WHY it is offering no other entities —
            // "not searched for" and "none exist" look identical in an empty list.
            pendingReview.quick_setup = quickRun;
            dispatch({ type: "SET_EXTRACTION_PROGRESS", pct: 50, message: quickRun ? "Wrapping up the entity…" : "Saving detected people…" });
          }

          if (event.event === "node_detected") {
            dispatch({ type: "SET_EXTRACTION_PROGRESS", pct: event.pct ?? 80, message: `Saving ${event.child?.entity_name ?? "entity"}…` });
            if (event.child?.entity_type === "person" && event.child?.entity_id) {
              dispatch({ type: "ADD_DETECTED_PERSON", person: { entity_id: event.child.entity_id, entity_name: event.child.entity_name ?? "" } as DetectedPerson });
            }
          }

          if (event.event === "node_detection_complete" && !extractionDone) {
            extractionDone = true;
            const created: number = event.created ?? 0;
            const total: number = event.total ?? 0;
            const verified: boolean = event.verified !== false;
            // A quick run detects nobody, so "All entities saved!" would be a claim about
            // work that never ran — the honest line names what actually happened.
            const message = quickRun
              ? "Entity ready"
              : verified
                ? "All entities saved!"
                : `${created}/${total} entities saved`;
            const review = pendingReview ?? shapeEntityExtractionData({}, []);
            if (!verified) {
              review.save_warning = `${created} of ${total} entities were saved — ${event.failed ?? 0} failed`;
            }
            dispatch({ type: "SET_EXTRACTION_PROGRESS", pct: 100, message });
            dispatch({ type: "SET_EXTRACTED_REVIEW", review });
            dispatch({ type: "SET_EXTRACTING", value: false });
            onAutoAdvance();
          }

          if (event.event === "node_extract_failed" && !extractionDone) {
            dispatch({ type: "SET_EXTRACTING", value: false });
            dispatch({ type: "SET_ERROR", error: event.message ?? "Extraction failed" });
          }

          if (event.event === "node_duplicate_found" && !extractionDone) {
            // Duplicate path has no child creation — mark done immediately.
            extractionDone = true;
            dispatch({ type: "SET_EXTRACTION_PROGRESS", pct: 100, message: "Extraction complete!" });
            dispatch({
              type: "SET_EXTRACTED_REVIEW",
              review: shapeEntityExtractionData(event.existing_entity ?? {}, []),
            });
            dispatch({ type: "SET_EXTRACTING", value: false });
            onAutoAdvance();
          }
        } catch {
          // malformed event — ignore
        }
      };

      ws.onerror = () => {
        dispatch({ type: "SET_EXTRACTING", value: false });
        dispatch({ type: "SET_ERROR", error: "Connection error during extraction" });
      };

      ws.onclose = (evt) => {
        if (!extractionDone) {
          // WS closed before node_detection_complete — surface whatever data we have
          if (pendingReview) {
            extractionDone = true;
            pendingReview.save_warning = "Connection closed before all entities were confirmed";
            dispatch({ type: "SET_EXTRACTION_PROGRESS", pct: 100, message: "Extraction complete" });
            dispatch({ type: "SET_EXTRACTED_REVIEW", review: pendingReview });
            onAutoAdvance();
          }
          dispatch({ type: "SET_EXTRACTING", value: false });
        }
      };
    },
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ── Discovery review (backlog #18) — confirm accepted candidates ───────────
  // Called from the Review step's Continue button. Persists only sub_entities still
  // `accepted`, via POST /entity/{id}/extraction/confirm — mirrors the payload shape
  // StepEntityCanvas.tsx used for the same endpoint.

  const confirmCandidates = useCallback(async (): Promise<void> => {
    const current = stateRef.current;
    const review = current.extractedReview;
    // Guards re-confirming (and double-creating) if the user goes Back to Review
    // after already confirming once.
    if (!review || !current.entityId || current.candidatesConfirmed) return;

    const selected = review.sub_entities.filter((s) => s.accepted);
    if (selected.length === 0) {
      dispatch({ type: "SET_CANDIDATES_CONFIRMED", value: true });
      return;
    }

    dispatch({ type: "SET_CONFIRMING_CANDIDATES", value: true });
    dispatch({ type: "SET_CONFIRM_CANDIDATES_ERROR", error: null });
    try {
      await apiFetch(`/entity/${current.entityId}/extraction/confirm`, {
        method: "POST",
        body: JSON.stringify({
          candidates: selected.map((se) => ({
            entity_name: se.suggested_name,
            entity_type: se.entity_type,
            entity_type_sub: se.entity_type_sub ?? undefined,
            entity_offering_type: se.entity_offering_type ?? undefined,
            entity_offering_delivery: se.entity_offering_delivery ?? undefined,
            evidence: se.description ?? undefined,
            confidence_score: se.confidence,
            relationship_type: se.relationship_type,
            role_title: se.role_title ?? undefined,
            is_part_of_parent: se.is_part_of_parent,
          })),
        }),
      });
      dispatch({ type: "SET_CANDIDATES_CONFIRMED", value: true });
    } catch (e) {
      dispatch({ type: "SET_CONFIRM_CANDIDATES_ERROR", error: (e as Error).message });
      throw e;
    } finally {
      dispatch({ type: "SET_CONFIRMING_CANDIDATES", value: false });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── PROJECT-077: manual-entry AI web-search resolver ──────────────────────

  const searchManualEntity = useCallback(async (
    description: string,
    entityTypeHint: string,
    clarifications: import("../types").ManualSearchClarification[],
  ): Promise<void> => {
    dispatch({ type: "SET_MANUAL_SEARCH_STATUS", value: "searching" });
    dispatch({ type: "SET_MANUAL_SEARCH_ERROR", error: null });
    try {
      const body = await apiFetch("/entity/search-resolve", {
        method: "POST",
        body: JSON.stringify({
          description,
          entity_type_hint: entityTypeHint,
          clarifications,
        }),
      });
      const data = body.data ?? {};
      dispatch({
        type: "SET_MANUAL_SEARCH_RESULT",
        candidates: data.resolved ? (data.candidates ?? []) : [],
        questions: data.resolved ? [] : (data.questions ?? []),
      });
    } catch (e) {
      dispatch({ type: "SET_MANUAL_SEARCH_ERROR", error: (e as Error).message });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const createEntityFromManualCandidate = useCallback(async (
    candidate: import("../types").ManualSearchCandidate | null,
    description: string,
    entityTypeHint: string,
    visibility: "public" | "private",
  ): Promise<string> => {
    // PROJECT-077 T0112/T0117 — no more silent already_in_raisy short-circuit here.
    // Wizard_Entity_Onboarding's handleResolveManual intercepts BEFORE this function runs
    // whenever candidate.existing_matches is non-empty, opening the shared merge-review
    // popup instead. By the time this function is called, either there were never any
    // possible matches, or the user already explicitly said "none of these" in that
    // popup — either way, the right thing here is always to create a new entity, never
    // to silently reuse existing_entity_id (that would ignore the user's own "none of
    // these" choice on the reject path).
    const headers = await AuthUtils.getAuthHeaders();
    const entityName = candidate?.entity_name || description.slice(0, 60).trim() || "New Entity";
    const createBody: Record<string, unknown> = {
      entity_name: entityName,
      entity_type: candidate?.entity_type || entityTypeHint,
      entity_type_sub: candidate?.entity_type_sub ?? undefined,
      entity_offering_type: candidate?.entity_offering_type ?? undefined,
      entity_offering_delivery: candidate?.entity_offering_delivery ?? undefined,
      entity_description: buildCandidateDescription(candidate, description),
      person_role: candidate?.role ?? undefined,
      entity_location: candidate?.location ?? undefined,
      // Resolved server-side into a real entity_relationships edge (person → company),
      // not stored on this entity directly — see _resolve_or_create_affiliation_entity.
      affiliation: candidate?.affiliation ?? undefined,
      entity_status: "prospect",
      visibility,
      entity_links: candidate?.entity_links ?? undefined,
    };

    const res = await fetch(`${API_URL}/api/v1/entity/create`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...headers },
      body: JSON.stringify(createBody),
    });
    const body = await res.json().catch(() => ({}));

    // 409 = duplicate — mirrors startSession's handling above so the existing
    // DuplicatePrompt/confirmDuplicate flow serves this path too (origin: "manual").
    if (res.status === 409) {
      const similarity: number =
        typeof body.data?.similarity_score === "number" ? body.data.similarity_score : 1.0;
      const existingId: string = body.data?.existing_entity_id;
      const existingName: string = body.data?.existing_entity?.entity_name ?? "";
      const existingType: string = body.data?.existing_entity?.entity_type ?? entityTypeHint;

      if (similarity >= 1.0) {
        dispatch({ type: "SET_ENTITY_ID", entityId: existingId });
        return existingId;
      }

      dispatch({
        type: "SET_DUPLICATE_DECISION",
        decision: {
          status: "pending",
          existingEntityId: existingId,
          existingEntityName: existingName,
          existingEntityType: existingType,
          similarity,
          createBody,
          origin: "manual",
          pendingUrl: candidate?.url ?? null,
        },
      });
      throw new Error("DUPLICATE_NEEDS_CONFIRMATION");
    }

    if (!res.ok) throw new Error(body.detail ?? body.message ?? `HTTP ${res.status}`);

    const entityId: string = body.data?.entity_id;
    if (!entityId) throw new Error(body.detail ?? body.message ?? `HTTP ${res.status}`);

    dispatch({ type: "SET_ENTITY_ID", entityId });
    return entityId;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Cleanup ────────────────────────────────────────────────────────────────

  const closeWs = useCallback(() => {
    wsRef.current?.close();
    wsRef.current = null;
  }, []);

  return {
    state,
    dispatch,
    // navigation
    advanceStep,
    back,
    minimize,
    // api
    validateUrl,
    startSession,
    confirmDuplicate,
    openExtractionSocket,
    confirmCandidates,
    searchManualEntity,
    createEntityFromManualCandidate,
    closeWs,
  };
}
